import {FC, useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useRecoilState, useRecoilStateLoadable, useRecoilValueLoadable, useSetRecoilState} from 'recoil';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import {Button, Tab, Tabs, Tooltip, Typography} from '@mui/material';
import TabPanel from '../../../ui/tab-panel/TabPanel';
import {Stack} from '@mui/system';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import NotFoundErrorInsideComponent from '../../../ui/not-found/NotFoundErrorInsideComponent';
import Fallback from '../../../ui/fallback/Fallback';
import General from './General';
import Status from '../../../ui/status/Status';
import DeviceInformation from './DeviceInformation';
import EditIcon from '@mui/icons-material/Edit';

import {
  contentTreeQueryFamily,
  currentServiceRequestQuery,
  reasonDelaysFamilyQuery,
} from '../../../store/service-requests/selectors';

import {deviceTypeDictQueryByCode} from '../../../store/deviceTypes/selectors';
import {EventBusContext} from '../../../providers/EventBus';
import {
  currentServiceRequestState,
  docsToReplaceState,
  serviceRequestActiveTabState,
} from '../../../store/service-requests/atoms';
import {companyByIdQuery} from '../../../store/company/selectors';
import {ServiceRequestFooter} from './ServiceRequestFooter';
import {Delayed} from './Delayed/Delayed';
import {useRedirectedContractsStore} from '../../../store/redirectedContracts';
import {get} from 'lodash';
import {getCodeWithArchivedStatus} from '../../../shared/helpers/getCodeWithArchivedStatus';

const tabStyle = {
  fontSize: '0.8rem',
};

const ServiceRequest: FC = () => {
  const {id} = useParams();
  const {redirectedContracts, removeContract} = useRedirectedContractsStore();
  const [{state: serviceRequestState, contents: serviceRequestContents}, setFetchedServiceRequest] =
    useRecoilStateLoadable(currentServiceRequestQuery(id));
  const [{state: deviceTypeState, contents: deviceTypeContents}, setDeviceType] =
    useRecoilStateLoadable(deviceTypeDictQueryByCode);
  const [{contents: contentTree, state: contentTreeState}, setForm] = useRecoilStateLoadable(
    contentTreeQueryFamily(id),
  );
  const [{contents: company, state: companyState}] = useRecoilStateLoadable(
    companyByIdQuery(serviceRequestState === 'hasValue' ? serviceRequestContents?.companyId : null),
  );
  const {contents: reasonDelays, state: stateDelays} = useRecoilValueLoadable(reasonDelaysFamilyQuery(id));
  const [serviceRequest, setServiceRequest] = useRecoilState(currentServiceRequestState);
  const [activeTab, setActiveTab] = useRecoilState(serviceRequestActiveTabState);
  const setDocsToReplace = useSetRecoilState(docsToReplaceState);
  const {bus} = useContext(EventBusContext);

  const isArchived = get(serviceRequestContents, 'archived', false);

  const [value, setValue] = useState(0);

  const navigate = useNavigate();

  const loading =
    serviceRequestState === 'loading' ||
    companyState === 'loading' ||
    deviceTypeState === 'loading' ||
    !deviceTypeContents ||
    !serviceRequestContents ||
    !company;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleBack = () => {
    const redirectedContract = get(redirectedContracts, id as string, null);
    if (redirectedContract) {
      removeContract(id as string);
      navigate(`/contracts/${redirectedContract}`, {state: {tab: 3}});
    } else {
      navigate('/serviceRequests');
    }
  };

  useEffect(() => {
    contentTreeState === 'hasValue' && serviceRequest && setForm(contentTree);

    serviceRequestState === 'hasValue' && setFetchedServiceRequest(serviceRequestContents);

    deviceTypeState === 'hasValue' && setDeviceType(deviceTypeContents);

    contentTreeState === 'hasError' && bus?.emit('error', [contentTree]);

    serviceRequestState === 'hasError' && bus?.emit('error', [serviceRequestContents]);

    deviceTypeState === 'hasError' && bus?.emit('error', [deviceTypeContents]);

    stateDelays === 'hasError' && bus?.emit('error', [reasonDelays]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRequestState, deviceTypeState, contentTreeState, stateDelays]);

  useEffect(() => {
    return () => {
      setServiceRequest(null);
      setActiveTab(null);
      setDocsToReplace([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{width: '100%'}}>
      <Paper sx={{width: '96%', margin: '0 auto', mt: 4, mb: 4}}>
        <Box sx={{padding: 2}}>
          <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
            <Stack direction="row" sx={{justifyContent: 'space-between', p: 1}}>
              {!loading && (
                <>
                  <Stack width="fit-content" direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body1">{`Заявка № ${serviceRequestContents.id}`}</Typography>
                    <Stack direction="row" alignItems="center" sx={{columnGap: '20px'}}>
                      <Typography variant="body1">{`${deviceTypeContents ? deviceTypeContents.name : ''}`}</Typography>
                      {serviceRequest?.executedDate ? (
                        <Typography variant="caption">
                          {`(выполнена ${new Intl.DateTimeFormat('ru-RU', {
                            timeZone: 'Europe/Moscow',
                            hour: 'numeric',
                            minute: 'numeric',
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                          }).format(new Date(serviceRequest.executedDate))})`}
                        </Typography>
                      ) : null}
                      {serviceRequest?.device?.deviceStatus?.moderationNeeds ? (
                        <Tooltip
                          PopperProps={{disablePortal: true}}
                          title={`Установлено неизвестное устройство №${serviceRequest.device.id} (!)\nНеобходима верификация`}
                        >
                          <EditIcon color="error" />
                        </Tooltip>
                      ) : null}
                      {serviceRequestContents.onRevision ? (
                        <Box
                          sx={{
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 0,
                            gap: '4px',
                            width: '139px',
                            height: '28px',
                            bgcolor: '#FFFFFF',
                            border: '1px solid #FF9F00',
                            borderRadius: '5px',
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '26px',
                            textAlign: 'center',
                            color: '#FF9F00',
                          }}
                        >
                          На доработке
                        </Box>
                      ) : null}
                    </Stack>
                  </Stack>
                  <Status
                    entity="serviceRequest"
                    border={serviceRequestContents.statusCode === 'CREATED' || isArchived}
                    code={getCodeWithArchivedStatus(serviceRequestContents.statusCode, isArchived)}
                  />
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={7}>
              <Button startIcon={<KeyboardReturnIcon />} onClick={handleBack}>
                Назад
              </Button>
              <Tabs value={value} onChange={handleChange}>
                <Tab
                  disabled={activeTab?.editMode}
                  onClick={() => setActiveTab({activeTab: 'general'})}
                  sx={tabStyle}
                  label="Общая информация"
                />
                <Tab
                  onClick={() =>
                    setActiveTab((prev) => (prev ? {...prev, activeTab: 'deviceInfo'} : {activeTab: 'deviceInfo'}))
                  }
                  sx={tabStyle}
                  label="Информация об объекте"
                />
                <Tab
                  disabled={activeTab?.editMode}
                  onClick={() => setActiveTab({activeTab: 'delayed'})}
                  sx={tabStyle}
                  label="Недопуски"
                />
              </Tabs>
            </Stack>
          </Box>
          <Box
            sx={{
              height: `${(document.body.querySelector('main')?.clientHeight as number) - 97 - 64 - 101}px`,
              overflow: 'auto',
            }}
          >
            <TabPanel value={value} index={0}>
              {serviceRequestState === 'hasError' ? (
                <NotFoundErrorInsideComponent to="/serviceRequests" />
              ) : loading ? (
                <Fallback />
              ) : (
                <General serviceRequest={serviceRequestContents} company={company} device={deviceTypeContents} />
              )}
            </TabPanel>
            <TabPanel value={value} index={1}>
              {serviceRequestState === 'hasError' ? (
                <NotFoundErrorInsideComponent to="/serviceRequests" />
              ) : loading ? (
                <Fallback />
              ) : (
                <DeviceInformation template={contentTree} serviceRequest={serviceRequestContents} />
              )}
            </TabPanel>
            <TabPanel value={value} index={2}>
              {serviceRequestState === 'hasError' ? (
                <NotFoundErrorInsideComponent to="/serviceRequests" />
              ) : loading ? (
                <Fallback />
              ) : (
                <Delayed reasonDelays={reasonDelays} />
              )}
            </TabPanel>
          </Box>
          <ServiceRequestFooter />
        </Box>
      </Paper>
    </Box>
  );
};

export default ServiceRequest;
